/**
 * Settings (ux-flows §1.H) — account, notifications, how-it-works, legal, sign out, and the App
 * Store-required account deletion. Trimmed to what candidate-web can honestly offer at Phase 1:
 * there is no profile-edit endpoint yet, and notifications are EMAIL (no push on the web —
 * DESIGN-NOTES delta c). Subscription / restore-purchases are IAP concepts that arrive with unlock
 * (WP-F11) and are deliberately absent here.
 *
 * Account deletion is the load-bearing piece: it wires the REAL anonymisation endpoint
 * (`DELETE /api/account`, WP-B18), behind irreversibility copy + a type-to-confirm gate. On success
 * it signs out and clears the local token, exactly as a destroyed account should.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { signOut } from '@/auth/session';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';

const CONFIRM_WORD = 'DELETE';

function BackLink() {
  return (
    <Link
      to="/"
      className="-ml-1 inline-flex items-center gap-1 rounded-md px-1 py-1 font-body text-[13px] font-medium text-ink-quiet transition-colors duration-ui ease-thread hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25"
    >
      ‹ References Ledger
    </Link>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="font-display text-[13px] font-semibold uppercase tracking-[0.06em] text-ink-quiet">{title}</h2>
      <div className="mt-2 rounded-lg border border-line bg-surface p-4">{children}</div>
    </section>
  );
}

/** The type-to-confirm account-deletion block (ux-flows §1.H). */
function DeleteAccount() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  // When the type-to-confirm block reveals, move focus straight to the input so the keyboard path
  // lands on the one thing the candidate now has to do.
  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);

  const del = useMutation({
    mutationFn: () => apiClient.requestAccountDeletion(),
    onSuccess: () => {
      // A destroyed account is a signed-out one: drop the local token and leave for sign-in.
      signOut();
      navigate('/sign-in', { replace: true });
    },
    onError: () => setError('We couldn’t delete your account just now. Try again in a moment.'),
  });

  const canDelete = confirmText.trim().toUpperCase() === CONFIRM_WORD;

  return (
    <section className="mt-6">
      <h2 className="font-display text-[13px] font-semibold uppercase tracking-[0.06em] text-coral-text">
        Delete account
      </h2>
      <div className="mt-2 rounded-lg border border-[rgba(163,77,46,0.35)] bg-surface p-4">
        {!open ? (
          <>
            <p className="font-body text-[13.5px] leading-relaxed text-ink">
              Permanently delete your account and personal details. This cannot be undone.
            </p>
            <Button variant="ghost" className="mt-3" onClick={() => setOpen(true)}>
              Delete my account
            </Button>
          </>
        ) : (
          <>
            <p className="font-body text-[13.5px] font-medium leading-relaxed text-ink">
              This is permanent. We’ll erase your account and personal details and close any in-flight requests.
              References that referees have already signed stay valid — their signatures aren’t ours to erase.
            </p>
            {error && (
              <p role="alert" className="mt-3 rounded-md bg-coral-tint px-3 py-2 font-body text-[12.5px] text-coral-text">
                {error}
              </p>
            )}
            <label htmlFor="delete-confirm" className="mt-4 block text-[12px] font-medium text-ink-quiet">
              Type <span className="font-semibold text-ink">{CONFIRM_WORD}</span> to confirm
            </label>
            <input
              id="delete-confirm"
              ref={confirmRef}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              aria-describedby="delete-confirm-help"
              className="mt-1.5 min-h-[44px] w-full rounded-md border border-line-2 bg-surface px-3.5 py-2 font-body text-[14px] text-ink outline-none transition-colors duration-ui ease-thread focus:border-coral-deep focus:ring-2 focus:ring-coral/25"
            />
            <p id="delete-confirm-help" className="sr-only">
              Enter the word {CONFIRM_WORD} to enable the delete button.
            </p>
            <div className="mt-4 flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setOpen(false);
                  setConfirmText('');
                  setError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="coral"
                size="sm"
                disabled={!canDelete || del.isPending}
                onClick={() => del.mutate()}
              >
                {del.isPending ? 'Deleting…' : 'Delete forever'}
              </Button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export function Settings() {
  const navigate = useNavigate();

  function onSignOut() {
    signOut();
    navigate('/sign-in', { replace: true });
  }

  return (
    <AppShell title="Settings">
      <BackLink />
      <h1 className="mt-2 font-display text-[22px] font-semibold tracking-[-0.02em] text-ink">Settings</h1>

      <Section title="Account">
        <p className="font-body text-[13.5px] leading-relaxed text-ink">
          You’re signed in with a one-time email code — there’s no password to manage.
        </p>
      </Section>

      <Section title="Notifications">
        <p className="font-body text-[13.5px] leading-relaxed text-ink">
          We email you when a referee responds — when they accept, when a reference is ready to unlock, and the odd
          gentle reminder. We contact your referees, never chase you.
        </p>
      </Section>

      <Section title="How it works">
        <p className="font-body text-[13.5px] leading-relaxed text-ink">
          You ask, we invite your referee, they record a few minutes in their own words and approve every line before
          it’s sent. You never have to contact them yourself.
        </p>
      </Section>

      <Section title="Invite a friend">
        <p className="font-body text-[13.5px] leading-relaxed text-ink">
          Share By My Word. When a friend you invite sends their first reference request, you’ll earn a free unlock.
        </p>
        <Button asChild variant="ghost" size="sm" className="mt-3">
          <Link to="/referral">Your invite code</Link>
        </Button>
      </Section>

      <Section title="Legal & support">
        <ul className="flex flex-col gap-2 font-body text-[13.5px] text-ink">
          <li>
            <a
              href="https://bymyword.com/privacy"
              className="text-sage-text underline-offset-2 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              Privacy policy
            </a>
          </li>
          <li>
            <a
              href="https://bymyword.com/terms"
              className="text-sage-text underline-offset-2 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              Terms of use
            </a>
          </li>
          <li>
            <a href="mailto:hello@bymyword.com" className="text-sage-text underline-offset-2 hover:underline">
              Contact support
            </a>
          </li>
        </ul>
      </Section>

      <Section title="Session">
        <Button variant="ghost" size="block" onClick={onSignOut}>
          Sign out
        </Button>
      </Section>

      <DeleteAccount />

      <p className="mt-8 pb-4 text-center font-body text-[11px] text-ink-quiet">
        bymyword.com — a reference you’ll keep forever.
      </p>
    </AppShell>
  );
}
